import { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Plus, Edit, Trash2, Loader2, X, Save, Users, Youtube, QrCode, Link as LinkIcon, Clock, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['Engineering', 'JEE', 'NEET'];

const emptyQuestion = {
  question: '',
  options: ['', '', '', ''],
  correct_answer: 0,
  explanation: '',
  marks: 1,
  negative_marks: 0
};

export default function AdminTestSeries() {
  const [seriesList, setSeriesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [editingSeries, setEditingSeries] = useState(null);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [saving, setSaving] = useState(false);
  const [studentEmail, setStudentEmail] = useState('');
  const [allowedStudents, setAllowedStudents] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Engineering',
    duration_minutes: 60,
    total_marks: 100,
    negative_marking: false,
    negative_marks_value: 0.25,
    price: '0',
    payment_link: '',
    payment_qr_url: '',
    is_free: false,
    youtube_solution_url: '',
    questions: [{ ...emptyQuestion }]
  });

  useEffect(() => {
    fetchSeriesList();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchSeriesList = async () => {
    try {
      const response = await axios.get(`${API}/admin/test-series`, getAuthHeaders());
      setSeriesList(response.data);
    } catch (error) {
      console.error('Error fetching test series:', error);
      toast.error('Failed to load test series');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (seriesId) => {
    if (!window.confirm('Are you sure you want to delete this test series?')) return;

    try {
      await axios.delete(`${API}/admin/test-series/${seriesId}`, getAuthHeaders());
      toast.success('Test series deleted successfully');
      fetchSeriesList();
    } catch (error) {
      console.error('Error deleting test series:', error);
      toast.error('Failed to delete test series');
    }
  };

  const openCreateModal = () => {
    setEditingSeries(null);
    setFormData({
      title: '',
      description: '',
      category: 'Engineering',
      duration_minutes: 60,
      total_marks: 100,
      negative_marking: false,
      negative_marks_value: 0.25,
      price: '0',
      payment_link: '',
      payment_qr_url: '',
      is_free: false,
      youtube_solution_url: '',
      questions: [{ ...emptyQuestion, options: ['', '', '', ''] }]
    });
    setShowModal(true);
  };

  const openEditModal = (series) => {
    setEditingSeries(series);
    setFormData({
      title: series.title,
      description: series.description,
      category: series.category,
      duration_minutes: series.duration_minutes,
      total_marks: series.total_marks,
      negative_marking: series.negative_marking,
      negative_marks_value: series.negative_marks_value,
      price: series.price,
      payment_link: series.payment_link || '',
      payment_qr_url: series.payment_qr_url || '',
      is_free: series.is_free,
      youtube_solution_url: series.youtube_solution_url || '',
      questions: series.questions?.length > 0 ? series.questions.map(q => ({
        ...q,
        options: [...q.options]
      })) : [{ ...emptyQuestion, options: ['', '', '', ''] }]
    });
    setShowModal(true);
  };

  const openAccessModal = async (series) => {
    setSelectedSeries(series);
    try {
      const response = await axios.get(`${API}/admin/test-series/${series.series_id}/students`, getAuthHeaders());
      setAllowedStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
      setAllowedStudents([]);
    }
    setShowAccessModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    // Calculate total marks from questions
    const totalMarks = formData.questions.reduce((sum, q) => sum + (q.marks || 1), 0);

    setSaving(true);
    try {
      const payload = {
        ...formData,
        total_marks: totalMarks,
        questions: formData.questions.map(q => ({
          question: q.question,
          options: q.options,
          correct_answer: q.correct_answer,
          explanation: q.explanation || '',
          marks: q.marks || 1,
          negative_marks: formData.negative_marking ? (q.negative_marks || formData.negative_marks_value) : 0
        }))
      };

      if (editingSeries) {
        await axios.put(`${API}/admin/test-series/${editingSeries.series_id}`, payload, getAuthHeaders());
        toast.success('Test series updated successfully');
      } else {
        await axios.post(`${API}/admin/test-series`, payload, getAuthHeaders());
        toast.success('Test series created successfully');
      }

      setShowModal(false);
      fetchSeriesList();
    } catch (error) {
      console.error('Error saving test series:', error);
      toast.error('Failed to save test series');
    } finally {
      setSaving(false);
    }
  };

  const grantAccess = async () => {
    if (!studentEmail.trim()) {
      toast.error('Please enter student email');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/test-series/${selectedSeries.series_id}/grant-access?student_email=${studentEmail}`,
        {},
        getAuthHeaders()
      );
      toast.success('Access granted successfully');
      setStudentEmail('');
      // Refresh students list
      const response = await axios.get(`${API}/admin/test-series/${selectedSeries.series_id}/students`, getAuthHeaders());
      setAllowedStudents(response.data);
    } catch (error) {
      console.error('Error granting access:', error);
      toast.error(error.response?.data?.detail || 'Failed to grant access');
    }
  };

  const revokeAccess = async (email) => {
    try {
      await axios.post(
        `${API}/admin/test-series/${selectedSeries.series_id}/revoke-access?student_email=${email}`,
        {},
        getAuthHeaders()
      );
      toast.success('Access revoked successfully');
      // Refresh students list
      const response = await axios.get(`${API}/admin/test-series/${selectedSeries.series_id}/students`, getAuthHeaders());
      setAllowedStudents(response.data);
    } catch (error) {
      console.error('Error revoking access:', error);
      toast.error('Failed to revoke access');
    }
  };

  const addQuestion = () => {
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, { ...emptyQuestion, options: ['', '', '', ''] }]
    }));
  };

  const removeQuestion = (index) => {
    if (formData.questions.length === 1) {
      toast.error('Test must have at least one question');
      return;
    }
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const updateQuestion = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      )
    }));
  };

  const updateOption = (qIndex, oIndex, value) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === qIndex 
          ? { ...q, options: q.options.map((o, j) => j === oIndex ? value : o) }
          : q
      )
    }));
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-brand-dark font-display">Test Series</h1>
          <p className="text-slate-600 mt-2">Total: {seriesList.length} test series</p>
        </div>
        <Button data-testid="create-test-series-btn" onClick={openCreateModal} className="btn-primary">
          <Plus className="h-5 w-5 mr-2" />
          Create Test Series
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="inline-block h-8 w-8 animate-spin text-brand-blue" />
        </div>
      ) : seriesList.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 mb-4">No test series created yet.</p>
          <Button onClick={openCreateModal} className="btn-primary">
            <Plus className="h-5 w-5 mr-2" />
            Create Your First Test Series
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {seriesList.map((series) => (
            <div key={series.series_id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-brand-dark">{series.title}</h3>
                    <span className={`text-xs font-semibold px-2 py-1 rounded ${
                      series.category === 'JEE' ? 'bg-orange-100 text-orange-700' :
                      series.category === 'NEET' ? 'bg-green-100 text-green-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {series.category}
                    </span>
                    {series.is_free ? (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">FREE</span>
                    ) : (
                      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">₹{series.price}</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mb-2">{series.description}</p>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" /> {series.duration_minutes} mins
                    </span>
                    <span className="flex items-center gap-1">
                      <Award className="h-4 w-4" /> {series.total_marks} marks
                    </span>
                    <span>{series.questions?.length || 0} questions</span>
                    {series.negative_marking && (
                      <span className="text-red-500">Negative marking: -{series.negative_marks_value}</span>
                    )}
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" /> {series.allowed_students?.length || 0} students
                    </span>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button onClick={() => openAccessModal(series)} variant="outline" size="sm" title="Manage Access">
                    <Users className="h-4 w-4" />
                  </Button>
                  <Button onClick={() => openEditModal(series)} variant="outline" size="sm">
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button onClick={() => handleDelete(series.series_id)} variant="destructive" size="sm">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-brand-dark">
                {editingSeries ? 'Edit Test Series' : 'Create Test Series'}
              </h2>
              <Button onClick={() => setShowModal(false)} variant="ghost" size="sm">
                <X className="h-5 w-5" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., JEE Main Mock Test 1"
                  />
                </div>
                <div>
                  <Label>Category *</Label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full h-10 px-3 border border-slate-200 rounded-md"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Test description..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Duration (mins)</Label>
                  <Input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 60 }))}
                  />
                </div>
                <div>
                  <Label>Price (₹)</Label>
                  <Input
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="0"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    checked={formData.is_free}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_free: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <Label>Free for all</Label>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    checked={formData.negative_marking}
                    onChange={(e) => setFormData(prev => ({ ...prev, negative_marking: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <Label>Negative Marking</Label>
                </div>
              </div>

              {formData.negative_marking && (
                <div className="w-48">
                  <Label>Negative Marks Value</Label>
                  <Input
                    type="number"
                    step="0.25"
                    value={formData.negative_marks_value}
                    onChange={(e) => setFormData(prev => ({ ...prev, negative_marks_value: parseFloat(e.target.value) || 0.25 }))}
                  />
                </div>
              )}

              {/* Payment Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="flex items-center gap-2"><LinkIcon className="h-4 w-4" /> Payment Link (UPI/Other)</Label>
                  <Input
                    value={formData.payment_link}
                    onChange={(e) => setFormData(prev => ({ ...prev, payment_link: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2"><QrCode className="h-4 w-4" /> Payment QR Image URL</Label>
                  <Input
                    value={formData.payment_qr_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, payment_qr_url: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-2"><Youtube className="h-4 w-4 text-red-500" /> YouTube Solution Video URL</Label>
                <Input
                  value={formData.youtube_solution_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, youtube_solution_url: e.target.value }))}
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>

              {/* Questions */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <Label className="text-lg">Questions ({formData.questions.length})</Label>
                  <Button type="button" onClick={addQuestion} variant="outline" size="sm">
                    <Plus className="h-4 w-4 mr-1" /> Add Question
                  </Button>
                </div>

                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {formData.questions.map((q, qIdx) => (
                    <div key={qIdx} className="p-4 border border-slate-200 rounded-lg bg-white">
                      <div className="flex justify-between items-start mb-3">
                        <Label className="font-semibold">Q{qIdx + 1}</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={q.marks}
                            onChange={(e) => updateQuestion(qIdx, 'marks', parseInt(e.target.value) || 1)}
                            className="w-20"
                            placeholder="Marks"
                          />
                          <span className="text-sm text-slate-500">marks</span>
                          <Button type="button" onClick={() => removeQuestion(qIdx)} variant="ghost" size="sm" className="text-red-500">
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <Textarea
                        value={q.question}
                        onChange={(e) => updateQuestion(qIdx, 'question', e.target.value)}
                        placeholder="Enter question..."
                        className="mb-3"
                        rows={2}
                      />

                      <div className="grid grid-cols-2 gap-2 mb-3">
                        {q.options.map((opt, oIdx) => (
                          <div key={oIdx} className="flex items-center gap-2">
                            <input
                              type="radio"
                              name={`correct-${qIdx}`}
                              checked={q.correct_answer === oIdx}
                              onChange={() => updateQuestion(qIdx, 'correct_answer', oIdx)}
                              className="w-4 h-4"
                            />
                            <span className="text-sm font-medium w-6">{String.fromCharCode(65 + oIdx)}.</span>
                            <Input
                              value={opt}
                              onChange={(e) => updateOption(qIdx, oIdx, e.target.value)}
                              placeholder={`Option ${String.fromCharCode(65 + oIdx)}`}
                              className="flex-1"
                            />
                          </div>
                        ))}
                      </div>

                      <Input
                        value={q.explanation}
                        onChange={(e) => updateQuestion(qIdx, 'explanation', e.target.value)}
                        placeholder="Explanation (optional)"
                        className="text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </form>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <Button type="button" onClick={() => setShowModal(false)} variant="outline">Cancel</Button>
              <Button onClick={handleSubmit} className="btn-primary" disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                {editingSeries ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Access Management Modal */}
      {showAccessModal && selectedSeries && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-brand-dark">Manage Access: {selectedSeries.title}</h2>
              <Button onClick={() => setShowAccessModal(false)} variant="ghost" size="sm">
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <div className="flex gap-2">
                <Input
                  value={studentEmail}
                  onChange={(e) => setStudentEmail(e.target.value)}
                  placeholder="Enter student email"
                  className="flex-1"
                />
                <Button onClick={grantAccess} className="btn-primary">Grant Access</Button>
              </div>

              <div>
                <Label className="mb-2 block">Students with Access ({allowedStudents.length})</Label>
                {allowedStudents.length === 0 ? (
                  <p className="text-sm text-slate-500">No students have access yet.</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {allowedStudents.map((student) => (
                      <div key={student.student_id} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                        <div>
                          <p className="font-medium text-sm">{student.name}</p>
                          <p className="text-xs text-slate-500">{student.email}</p>
                        </div>
                        <Button onClick={() => revokeAccess(student.email)} variant="destructive" size="sm">
                          Revoke
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-slate-200">
              <Button onClick={() => setShowAccessModal(false)} variant="outline" className="w-full">Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
